import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
  Divider,
  useTheme,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Shield,
  CheckCircle,
} from 'lucide-react';
import logo from '../assets/logo-perfume-stories-light.png';
import logoDark from '../assets/logo-perfume-stories-dark.png';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AdminSetup = ({ onSetupComplete }) => {
  const [step, setStep] = useState(1); // 1: Check, 2: Create Admin, 3: Success
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adminExists, setAdminExists] = useState(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const theme = useTheme();

  const currentLogo = theme.palette.mode === 'dark' ? logoDark : logo;

  // Check if admin already exists
  useEffect(() => {
    const checkAdminExists = async () => {
      try {
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        
        let hasAdmin = false;
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          if (userData.userType === 'admin' || userData.role === 'admin') {
            hasAdmin = true;
          }
        });
        
        if (hasAdmin) {
          setAdminExists(true);
          setStep(3);
        } else {
          setAdminExists(false);
          setStep(2);
        }
      } catch (err) {
        console.error('Error checking admin:', err);
        setAdminExists(false);
        setStep(2);
      } finally {
        setCheckingAdmin(false);
      }
    };

    checkAdminExists();
  }, []);

  const validateForm = () => {
    if (!email || !password || !confirmPassword || !name) {
      setError('All fields are required');
      return false;
    }

    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      setError('Invalid email format');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (name.length < 2) {
      setError('Name must be at least 2 characters');
      return false;
    }

    return true;
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create user document in Firestore with admin role
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        name: name,
        displayName: name,
        role: 'admin',
        userType: 'admin', // This is the key field used throughout the app
        status: 'Active',
        createdAt: new Date().toISOString(),
        permissions: {
          dashboard: true,
          grn: true,
          stockTransfer: true,
          inventory: true,
          stockHistory: true,
          billing: true,
          returns: true,
          customers: true,
          creditors: true,
          expenses: true,
          reports: true,
          sales: true,
          staff: true,
          activityLogs: true,
          settings: true,
          modules: true,
          viewProfitLoss: true,
          deleteBilling: true,
          deleteInventory: true,
          deleteGRN: true,
          deleteExpenses: true,
          deleteCustomers: true,
        }
      });

      // Sign out the newly created admin
      await signOut(auth);

      setSuccess(`Admin account created successfully! Email: ${email}`);
      setStep(3);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setName('');
    } catch (err) {
      console.error('Error creating admin:', err);
      
      let errorMessage = 'Failed to create admin account';
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'Email is already in use';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (checkingAdmin) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #000000 0%, #0f0f0f 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #000000 0%, #0f0f0f 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
            }}
          >
            <Box
              component="img"
              src={currentLogo}
              alt="Perfume Stories"
              sx={{
                width: 140,
                height: 'auto',
                maxHeight: 140,
                objectFit: 'contain',
                borderRadius: 0,
                mb: 3,
                padding: 0,
                border: `0px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
              }}
            />

            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Admin Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first super admin account
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Admin Already Exists */}
          {adminExists && step === 3 && (
            <>
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle size={20} />
                  <Typography>Admin account already exists</Typography>
                </Box>
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your system is ready to use. Please log in with your admin credentials.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={onSetupComplete}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Go to Login
              </Button>
            </>
          )}

          {/* Create Admin Form */}
          {step === 2 && (
            <>
              <Card sx={{ mb: 3, backgroundColor: theme.palette.action.hover }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Shield size={20} color="primary" />
                    <Typography variant="subtitle2" fontWeight="bold">
                      Super Admin Permissions
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    <Chip label="Manage Users" size="small" variant="outlined" />
                    <Chip label="Manage Inventory" size="small" variant="outlined" />
                    <Chip label="Manage Sales" size="small" variant="outlined" />
                    <Chip label="Manage Purchases" size="small" variant="outlined" />
                    <Chip label="Manage Expenses" size="small" variant="outlined" />
                    <Chip label="Manage Billing" size="small" variant="outlined" />
                    <Chip label="View Reports" size="small" variant="outlined" />
                    <Chip label="Manage Settings" size="small" variant="outlined" />
                  </Stack>
                </CardContent>
              </Card>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleCreateAdmin}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <User size={20} color="gray" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Mail size={20} color="gray" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                    helperText="Minimum 8 characters"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock size={20} color="gray" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={loading}
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock size={20} color="gray" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            disabled={loading}
                          >
                            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Shield size={20} />}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  >
                    {loading ? 'Creating Admin...' : 'Create Super Admin'}
                  </Button>
                </Stack>
              </form>
            </>
          )}

          {/* Success State */}
          {step === 3 && success && (
            <>
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle size={20} />
                  <Typography>{success}</Typography>
                </Box>
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your super admin account has been created successfully. You can now log in with your credentials.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={onSetupComplete}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Go to Login
              </Button>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              © 2026 Perfume Stories. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminSetup;
